import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { 
  Download, Loader2, Image as ImageIcon, X, ArrowLeft, 
  Sparkles, Check, RefreshCw, Zap, Type
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Footer from '../Footer';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TextToImagePage = () => {
  const navigate = useNavigate();
  
  // Simple state
  const [prompt, setPrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [resolution, setResolution] = useState('720p');
  const [selectedModel, setSelectedModel] = useState('standard');
  
  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [resultImageUrl, setResultImageUrl] = useState(null);
  const [error, setError] = useState(null);

  // Generate image
  const generateImage = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a text prompt');
      return;
    }
    
    setIsGenerating(true);
    setProgress(0);
    setError(null);
    setResultImageUrl(null);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      formData.append('prompt', prompt);
      formData.append('aspect_ratio', aspectRatio);
      formData.append('resolution', resolution);
      formData.append('model', selectedModel);
      
      const response = await axios.post(`${API}/creator-ai/text-to-image/generate`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 120000
      });
      
      if (response.data.success) {
        setJobId(response.data.job_id);
        toast.success('Image generation started!');
        pollStatus(response.data.job_id);
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to start generation');
      setIsGenerating(false);
      toast.error(error.response?.data?.detail || 'Failed to start generation');
    }
  };

  // Poll job status
  const pollStatus = async (id) => {
    const token = localStorage.getItem('token');
    let attempts = 0;
    const maxAttempts = 60;
    
    const poll = async () => {
      if (attempts >= maxAttempts) {
        setError('Generation timed out. Please try again.');
        setIsGenerating(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/creator-ai/text-to-image/status/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.success) {
          const { status, progress: jobProgress, image_url, error: jobError } = response.data;
          
          setProgress(jobProgress || 0);
          
          if (status === 'completed' && image_url) {
            setResultImageUrl(image_url);
            setIsGenerating(false);
            toast.success('Your image is ready!');
            return;
          } else if (status === 'failed') {
            setError(jobError || 'Generation failed');
            setIsGenerating(false);
            toast.error(jobError || 'Generation failed');
            return;
          }
        }
        
        attempts++;
        setTimeout(poll, 3000);
      } catch (error) {
        console.error('Poll error:', error);
        attempts++;
        setTimeout(poll, 3000);
      }
    };
    
    poll();
  };

  // Reset to start
  const resetAll = () => {
    setPrompt('');
    setAspectRatio('1:1');
    setResolution('720p');
    setSelectedModel('standard');
    setJobId(null);
    setProgress(0);
    setResultImageUrl(null);
    setError(null);
    setIsGenerating(false);
  };

  const modelOptions = [
    { value: 'standard', label: 'Standard Quality', desc: 'High-quality text-to-image generation' }
  ];

  const aspectRatioOptions = [
    { value: '1:1', label: 'Square', desc: '1:1', icon: '⬜' },
    { value: '16:9', label: 'Landscape', desc: '16:9', icon: '▭' },
    { value: '9:16', label: 'Portrait', desc: '9:16', icon: '▯' }
  ];

  const resolutionOptions = [
    { value: '720p', label: '720p HD' },
    { value: '1080p', label: '1080p Full HD' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/image-generator')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-3"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Back to Creator AI</span>
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center">
              <ImageIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Text to Image</h1>
              <p className="text-sm text-gray-500">Create stunning images from text descriptions</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm">
          <CardContent className="p-6 sm:p-8">
            
            {/* Show result if image is ready */}
            {resultImageUrl ? (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 rounded-full mb-4">
                    <Check className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium text-green-700">Your Image is Ready!</span>
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                    Download Your AI Image
                  </h2>
                </div>

                {/* Image Preview */}
                <Card className="overflow-hidden">
                  <div className="bg-gray-100 flex items-center justify-center p-4">
                    <img
                      src={resultImageUrl}
                      alt="Generated"
                      className="max-h-[600px] w-auto rounded-lg shadow-lg" loading="lazy" decoding="async"/>
                  </div>
                  <CardContent className="p-4">
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">{resolution} Quality</span>
                      <span className="px-3 py-1 bg-pink-100 text-pink-700 text-xs rounded-full">AI Generated</span>
                      <span className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">{aspectRatio}</span>
                    </div>
                    
                    <Button
                      onClick={() => window.open(resultImageUrl, '_blank')}
                      className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:opacity-90"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download Image
                    </Button>
                  </CardContent>
                </Card>

                {/* Actions */}
                <div className="flex justify-center">
                  <Button variant="outline" onClick={resetAll}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Create Another Image
                  </Button>
                </div>
              </div>
            ) : isGenerating ? (
              /* Generating state */
              <div className="space-y-8 text-center max-w-lg mx-auto">
                <div>
                  <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center animate-pulse">
                    <ImageIcon className="h-12 w-12 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Creating Your Image
                  </h2>
                  <p className="text-gray-600">Please wait while AI generates your image...</p>
                </div>

                <div className="space-y-2">
                  <Progress value={progress} className="h-3" />
                  <p className="text-sm text-gray-500">{progress}% complete</p>
                </div>

                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm text-gray-600">
                    Using AI to create an image from your text prompt
                  </p>
                </div>

                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                    <p className="text-red-700">{error}</p>
                  </div>
                )}
              </div>
            ) : (
              /* Input form */
              <div className="space-y-6">
                {/* Hero */}
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full mb-4">
                    <Sparkles className="h-4 w-4 text-purple-600" />
                    <span className="text-sm font-medium text-purple-700">AI Image Generation</span>
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                    Create Images from Text
                  </h2>
                  <p className="text-gray-600 max-w-md mx-auto">
                    Describe your vision and let AI bring it to life
                  </p>
                </div>

                {/* Text Prompt */}
                <div className="max-w-md mx-auto">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Step 1: Describe Your Image</label>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="Example: A serene mountain landscape at sunset with vibrant orange and purple skies, snow-capped peaks, and a tranquil lake reflecting the colors..."
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:outline-none resize-none"
                    rows="5"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    💡 Tip: Be detailed and specific for best results
                  </p>
                </div>

                {/* Aspect Ratio */}
                <div className="max-w-md mx-auto">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Step 2: Choose Aspect Ratio</label>
                  <div className="grid grid-cols-3 gap-3">
                    {aspectRatioOptions.map((option) => (
                      <div
                        key={option.value}
                        onClick={() => setAspectRatio(option.value)}
                        className={`p-4 border-2 rounded-xl cursor-pointer transition-all text-center ${
                          aspectRatio === option.value
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-purple-300'
                        }`}
                      >
                        <div className="text-3xl mb-2">{option.icon}</div>
                        <p className="font-medium text-gray-900 text-sm">{option.label}</p>
                        <p className="text-xs text-gray-500">{option.desc}</p>
                        {aspectRatio === option.value && (
                          <Check className="h-4 w-4 text-purple-600 mx-auto mt-1" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Resolution */}
                <div className="max-w-md mx-auto">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Step 3: Select Resolution</label>
                  <div className="grid grid-cols-2 gap-3">
                    {resolutionOptions.map((option) => (
                      <div
                        key={option.value}
                        onClick={() => setResolution(option.value)}
                        className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                          resolution === option.value
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-purple-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-gray-900">{option.label}</p>
                          {resolution === option.value && (
                            <Check className="h-5 w-5 text-purple-600" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Quality Info - Single model */}
                <div className="max-w-md mx-auto">
                  <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border-2 border-purple-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Check className="h-5 w-5 text-purple-600" />
                      <p className="font-medium text-gray-900">High-Quality Generation</p>
                    </div>
                    <p className="text-sm text-gray-600">Using Higgsfield AI Soul - flagship text-to-image model</p>
                  </div>
                </div>

                {/* Generate Button */}
                <div className="flex justify-center pt-4">
                  <Button
                    onClick={generateImage}
                    disabled={!prompt.trim()}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:opacity-90 px-8 py-6 text-lg"
                  >
                    <Zap className="h-5 w-5 mr-2" />
                    Generate Image
                  </Button>
                </div>

                {/* Info */}
                <div className="text-center text-sm text-gray-500 space-y-1">
                  <p>💳 15 credits per image</p>
                  <p>⏱️ Processing time: 30-60 seconds</p>
                </div>
              </div>
            )}
            
          </CardContent>
        </Card>
      </div>

      <div className="mb-24"></div>
      <Footer />
    </div>
  );
};

export default TextToImagePage;
